import React from "react";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../../i18n";
import InputV2 from "@ui/Input/InputV2";
import useInput from "@ui/lib/hooks/useInput";
import type { Project } from "../../../../../api/useProjects";
import type { EventDiagnosed } from "../../../../../api/useEvents";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import Text from "@ui/Text";
import formatDate from "@ui/i18n/formatDate";
import { getDefaultFaultEventNames } from "diagnostics-domain/faultEvent";
import { isLeft } from "fp-ts/lib/Either";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import { usePost } from "../../../../../api/useCall";
import { useRevalidate } from "../../../../../api/useQuery";
import useToasts from "@ui/Toast/useToasts";
import { useNavigate } from "react-router";
import { toPath } from "../../../../../router/helpers/path";

const PATH = "/projects/:projectId(uuid)/faultEvents/grouping" as const;
const REVALIDATE_PATH = "/projects/:projectId(uuid)/faultEvents" as const;
const MIN_GROUPED_EVENTS = 2;

interface ModalFormGroupEventsProps {
  project: Project;
  events: EventDiagnosed[];
  onRequestClose: () => void;
  onSubmitSuccess: () => void;
}

const ModalFormGroupEvents = ({
  project,
  events,
  onRequestClose,
  onSubmitSuccess,
}: ModalFormGroupEventsProps) => {
  const groupEvents = usePost(PATH);
  const revalidate = useRevalidate();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { value: groupName, onChange: onGroupNameChange, dirty: groupNameDirty } = useInput("");

  const trimmedGroupName = groupName.trim();
  const errorGroupName = React.useMemo(() => {
    if (groupNameDirty && trimmedGroupName.length === 0) {
      return t("project.fault_event.modal.form.group_events.input.error.empty_field");
    }

    if (isLeft(short(io.string).decode(trimmedGroupName))) {
      return t(
        "project.fault_event.modal.form.input.fault.name.error.cannot_exceed_100_characters",
      );
    }

    return;
  }, [groupNameDirty, trimmedGroupName]);

  const finalEventName = React.useMemo(() => {
    if (trimmedGroupName.length === 0 || events.length === 0) return null;

    const earliestStartAt = new Date(Math.min(...events.map(event => event.startsAt.getTime())));
    return getDefaultFaultEventNames([trimmedGroupName], earliestStartAt);
  }, [events, trimmedGroupName]);

  const canSubmit =
    trimmedGroupName.length > 0 &&
    errorGroupName === undefined &&
    events.length >= MIN_GROUPED_EVENTS;

  const handleSubmit = React.useCallback(async () => {
    if (!canSubmit || finalEventName === null) return false;

    try {
      const { body } = await groupEvents({
        routeParams: { projectId: project.id },
        body: {
          name: finalEventName,
          faultEventIds: events.map(event => event.id),
        },
      });
      await revalidate(REVALIDATE_PATH);
      void navigate(
        toPath("/projects/:projectId/fault-events/:faultEventId/fault", {
          projectId: project.id,
          faultEventId: body.groupedEvent.id,
        }),
        { replace: true },
      );
      onSubmitSuccess();
      addToast("success", t("project.fault_event.modal.form.group_events.toast.success"));
      return true;
    } catch (error) {
      addToast("error", t("errors.backend_error"));
      return false;
    }
  }, [
    addToast,
    canSubmit,
    events,
    finalEventName,
    groupEvents,
    navigate,
    onSubmitSuccess,
    project.id,
    revalidate,
  ]);

  return (
    <ModalForm
      className={modalForm}
      onSubmit={canSubmit ? handleSubmit : false}
      onRequestClose={onRequestClose}
      okButtonName={t("project.fault_event.modal.form.group_events.button.name.ok")}
    >
      <InputV2
        className={input}
        type="text"
        label={t("project.fault_event.modal.form.group_events.input.name.label")}
        hint={t("project.fault_event.modal.form.group_events.input.name.hint")}
        value={groupName}
        onChange={onGroupNameChange}
        error={errorGroupName}
        required
      />

      {finalEventName !== null && (
        <Text type="body1SemiBold">
          {t("project.fault_event.modal.form.group_events.summary.name", finalEventName)}
        </Text>
      )}

      <div className={eventList}>
        <Text type="body1SemiBold" className={eventListTitle}>
          {t("project.fault_event.modal.form.group_events.summary.events", events.length)}
        </Text>
        <div className={eventListHeader}>
          <Text type="body2SemiBold">
            {t("project.fault_event.modal.form.group_events.summary.events.name")}
          </Text>
          <Text type="body2SemiBold">
            {t("project.fault_event.modal.form.group_events.summary.events.startsAt")}
          </Text>
          <Text type="body2SemiBold">
            {t("project.fault_event.modal.form.group_events.summary.events.endsAt")}
          </Text>
        </div>
        {events.map(event => (
          <div className={eventListRow} key={event.id}>
            <Text>{event.name}</Text>
            <Text>{formatDate(event.startsAt, "slash", project.timezone)}</Text>
            <Text>{formatDate(event.endsAt, "slash", project.timezone)}</Text>
          </div>
        ))}
      </div>
    </ModalForm>
  );
};

export default ModalFormGroupEvents;

//#region CSS
const modalForm = css`
  margin: 12px 0;
`;

const input = css`
  width: fit-content;
  min-width: 368px;
`;

const eventList = css`
  display: grid;
  gap: ${spacings.xs};
`;

const eventListTitle = css`
  margin-top: ${spacings.s};
`;

const eventListHeader = css`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(120px, 0.8fr) minmax(120px, 0.8fr);
  gap: ${spacings.s};
  padding-bottom: ${spacings.xs};
  border-bottom: 1px solid ${colors.titanium200};
`;

const eventListRow = css`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(120px, 0.8fr) minmax(120px, 0.8fr);
  gap: ${spacings.s};

  & > * {
    overflow-wrap: anywhere;
  }
`;
//#endregion
